import { useRef } from 'react';
import { captureException } from '@sentry/react';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import type { PlaylistInput } from '@graphql/reactQuery';
import { usePrefetchAudioOnDelayedHover } from '@hooks/usePrefetchAudioOnDelayedHover';
import { loadTrack, useAudioController } from '@store/audio/AudioController';
import { togglePlayPause } from '@store/audio/AudioEngineHTML5';
import { setPlaylistUpNext } from '@store/audio/AudioFetcher';
import { setActivePlaylist, setPlaybackId } from '@store/audio/AudioMeta';

export const usePlayButton = ({
  isAlbum,
  trackId,
  playbackId,
  audioSrc,
  playlist,
  playlistId,
  isSecretPreviewPage = false,
}: {
  isAlbum: boolean;
  trackId: string | null;
  audioSrc?: string;
  playlist?: PlaylistInput | null;
  playbackId?: string;
  playlistId?: string;
  isSecretPreviewPage?: boolean;
}) => {
  const {
    playing,
    loading: audioLoading,
    ready,
    activeTrackId,
    playbackId: globalPlaybackId,
  } = useAudioController();

  const ref = useRef<HTMLButtonElement>(null);

  usePrefetchAudioOnDelayedHover({ hoverRef: ref, trackId, duration: 200 });

  let isActiveTrack: boolean | undefined;

  if (playbackId) {
    isActiveTrack = playbackId === globalPlaybackId;
  } else {
    isActiveTrack = trackId === activeTrackId;
  }

  const isDisabled = audioSrc ? false : !trackId || isAlbum;

  // In the Draft preview page we don't have access to the track ID yet but we do have an audio source.
  // isActiveTrack will always be false since trackId is undefined, so we should only check if playing
  // is true
  const isPlaying = isSecretPreviewPage ? playing : isActiveTrack && playing;

  const isLoading = isActiveTrack && audioLoading;

  const onPlayClick = useStableCallback(async () => {
    if (isDisabled) return;
    if (isSecretPreviewPage && playing && ready) {
      togglePlayPause();
    } else if (!isActiveTrack && (!!trackId || !!audioSrc)) {
      if (playlistId && trackId) {
        setPlaylistUpNext(playlistId, trackId);
      } else {
        await loadTrack({
          trackId,
          audioSrc,
          playlist,
          addToQueue: !playlistId,
        }).catch(captureException);
      }
      if (playbackId) setPlaybackId(playbackId);
      if (!playlistId) setActivePlaylist(null, []);
    } else if (ready) {
      togglePlayPause();
    }
  });

  return { onPlayClick, ref, isLoading, isPlaying, isDisabled };
};
